import { useEffect, useRef } from 'react';
import { prefillVmLevelFields } from '../components/VMBasedMigrationsConfig/helpers';
import { handleError } from '../../../../lib/helpers';
import { useNotificationContext } from '../../../../providers';

const loadRedeployData = async ({
    appliance,
    cloudAccount,
    form,
    fetchAttributes,
    notifyError,
    subscriptions,
    setRegions,
    setResourceGroups,
    setVirtualNetworks,
    setSubnets,
    setLoadingRegionData,
}) => {
    const cloud_properties = appliance.cloud_properties || {};
    const { subscription_id, region } = cloud_properties;
    const subscription = subscriptions.find((candidate) => candidate.id === subscription_id);
    if (subscription) {
        setRegions(subscription.regions || []);
        form.setFieldsValue({ subscription: subscription_id });

        if (region) {
            form.setFieldsValue({ region });
            setLoadingRegionData(true);
            try {
                const response = await fetchAttributes({
                    cloudAccountId: cloudAccount.id,
                    params: { region, subscription_id },
                }).unwrap();

                const subscriptionData = response?.properties?.subscriptions?.[0];
                if (subscriptionData) {
                    setResourceGroups(subscriptionData.resource_groups || []);
                    const networks = subscriptionData.regions?.[0]?.networks || [];
                    setVirtualNetworks(networks);

                    if (cloud_properties.resource_group) {
                        form.setFieldsValue({ resourceGroup: cloud_properties.resource_group });
                    }

                    const vnet = cloud_properties.virtual_network;
                    if (vnet?.name && vnet?.resource_group) {
                        const network = networks.find((_network) => {
                            return _network.name === vnet.name && _network.resource_group === vnet.resource_group;
                        });
                        if (network) {
                            setSubnets(network.subnets || []);
                            form.setFieldsValue({ virtualNetwork: `${vnet.name}|${vnet.resource_group}` });
                        }
                    }
                }
            } catch (error) {
                handleError(error, notifyError, 'Failed to load region attributes');
            } finally {
                setLoadingRegionData(false);
            }
        }
    }
};

export const useApplianceRedeployPrefill = ({
    isRedeploy,
    cloudAccount,
    subscriptions,
    form,
    fetchAttributes,
    prefillCommonFromAppliance,
    setRegions,
    setResourceGroups,
    setVirtualNetworks,
    setSubnets,
    setInstanceTags,
    setLoadingRegionData,
}) => {
    const { notifyError } = useNotificationContext();
    const hasPrefilledRef = useRef(false);

    useEffect(() => {
        if (!hasPrefilledRef.current && isRedeploy && !!cloudAccount?.appliance && !!subscriptions.length) {
            hasPrefilledRef.current = true;
            const appliance = cloudAccount.appliance;
            const cloud_properties = appliance.cloud_properties;

            if (cloud_properties.vapp_name) {
                form.setFieldsValue({ vmName: cloud_properties.vapp_name });
            }
            if (cloud_properties.tags && typeof cloud_properties.tags === 'object') {
                setInstanceTags(cloud_properties.tags);
            }
            prefillCommonFromAppliance(appliance);
            if (appliance.vm_level_migration_enabled) {
                prefillVmLevelFields(appliance, form);
            }
            if (cloud_properties.network) {
                const eth0 = cloud_properties.network.interfaces?.eth0 || {};
                form.setFieldsValue({
                    subnet: eth0.network_name,
                    assignPublicIp: eth0.assign_public_ip ? 'dynamic' : 'none',
                    staticPrivateIp: eth0.ip_adsdr || '',
                });
            }

            if (cloud_properties.subscription_id) {
                loadRedeployData({
                    appliance,
                    cloudAccount,
                    form,
                    fetchAttributes,
                    notifyError,
                    subscriptions,
                    setRegions,
                    setResourceGroups,
                    setVirtualNetworks,
                    setSubnets,
                    setLoadingRegionData,
                });
            }
        }
    }, [isRedeploy, cloudAccount, subscriptions]);
};

